import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ChangePinModal } from '@/components/user/ChangePinModal';
import { PinSetupModal } from '@/components/user/PinSetupModal';
import { ROUTES } from '@/constants/routes';
import { useUserAuth } from '@/context/UserAuthContext';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { notify, userAccountService } from '@/services';

export function UserSecurityPage() {
  const navigate = useNavigate();
  const { user, logout, persistUser } = useUserAuth();
  const pinEnabled = user?.pinEnabled ?? false;
  const [showSetupPinModal, setShowSetupPinModal] = useState(false);
  const [showChangePinModal, setShowChangePinModal] = useState(false);

  return (
    <div className="space-y-6 pb-8 sm:pb-12">
      <div>
        <h1 className="text-2xl font-bold text-brand-navy">Security</h1>
        <p className="text-sm text-brand-muted">Manage your password and transaction PIN</p>
      </div>

      <ChangePasswordForm onPasswordChanged={() => void logout().then(() => navigate(ROUTES.login))} />

      <Card className="p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
              <ShieldCheck className="h-5 w-5" strokeWidth={2} />
            </span>
            <div>
              <h2 className="font-bold text-brand-navy">Transaction PIN</h2>
              <p className="mt-1 text-sm text-brand-muted">
                {pinEnabled
                  ? 'Your transaction PIN is active. Change it anytime if you need to.'
                  : 'Set a 4-digit PIN to authorize payments from your wallet.'}
              </p>
            </div>
          </div>
          <Button onClick={() => (pinEnabled ? setShowChangePinModal(true) : setShowSetupPinModal(true))}>
            {pinEnabled ? 'Change PIN' : 'Set up PIN'}
          </Button>
        </div>
      </Card>

      <PinSetupModal
        open={showSetupPinModal}
        onClose={() => setShowSetupPinModal(false)}
        onSuccess={persistUser}
      />

      <ChangePinModal
        open={showChangePinModal}
        onClose={() => setShowChangePinModal(false)}
        onSuccess={persistUser}
      />
    </div>
  );
}

function ChangePasswordForm({ onPasswordChanged }: { onPasswordChanged: () => void }) {
  const { loading, run } = useAsyncAction();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      notify.error('New password and confirmation do not match');
      return;
    }

    if (newPassword.length < 6 || newPassword.length > 12) {
      notify.error('Password must be 6–12 characters');
      return;
    }

    await run(async () => {
      try {
        const res = await userAccountService.changePassword(currentPassword, newPassword);
        if (!res.success) throw new Error(res.message || 'Failed to change password');
        notify.success(res.message || 'Password changed successfully', {
          description: 'Redirecting to login…',
        });
        setTimeout(onPasswordChanged, 1500);
      } catch (err) {
        notify.apiError(err, 'Failed to change password');
      }
    });
  }

  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <KeyRound className="h-5 w-5" strokeWidth={2} />
        </span>
        <div>
          <h2 className="font-bold text-brand-navy">Change password</h2>
          <p className="text-xs text-brand-muted">6–12 characters. You will be signed out after changing.</p>
        </div>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <Input
          label="Current password"
          name="currentPassword"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="New password"
            name="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="6–12 characters"
            autoComplete="new-password"
            required
          />
          <Input
            label="Confirm new password"
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        <Button type="submit" loading={loading} loadingText="Updating…">
          Update password
        </Button>
      </form>
    </Card>
  );
}
